import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { StateEnum, TeacherActivity, TeacherActivityTargetLink, TeacherTarget } from "@isophi/pap-api";
import { ManagerActivityTargetLink, ManagerTarget, StateEnum as ManagerStateEnum } from '@isophi/mng-api';
import { finalize, forkJoin, Observable, takeUntil, tap } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { targetStateData } from '../../constants/data';
import { BaseComponent } from '../base-component/base.component';
import { ToastService } from '../../services/toast.service';
import { BaseModeEnum } from '../portfolio-plan-base-item/portfolio-plan-base-item.component';

@Component({
    selector: 'isophi-app-portfolio-linked-targets',
    templateUrl: './portfolio-linked-targets.component.html',
    styleUrls: ['./portfolio-linked-targets.component.scss'],
    standalone: false
})
export class PortfolioLinkedTargetsComponent extends BaseComponent implements OnInit {
  @Input() teacherActivityId!: string;
  @Input() allowEditMode = true;
  @Input() useAbsoluteHref = false;

  @Input() fetchTeacherActivityTargetLinks!: (teacherActivityId: string) => Observable<TeacherActivityTargetLink[] | ManagerActivityTargetLink[]>;
  @Input() fetchTeacherTargets!: () => Observable<TeacherTarget[] | ManagerTarget[]>;
  @Input() deleteTeacherActivityTargetLink!: (linkId: string) => Observable<void>;
  @Input() createTeacherActivityTargetLink!: (teacherActivityId: string, teacherTargetId: string) => Observable<TeacherActivityTargetLink>;
  @Input() buildTeacherTargetRouteLink!: (teacherTargetId: string) => string[] | string;

  @Output() teacherActivityTargetLinksUpdated = new EventEmitter<TeacherActivity>();
  @Output() teacherActivityTargetLinksCountUpdated = new EventEmitter<number>();

  loadingTeacherActivityTargetLinks = false;
  loadingTeacherTargets = false;

  teacherActivityTargetLinks: TeacherActivityTargetLink[] = [];
  teacherTargets: TeacherTarget[] = [];

  existingTargetUuids: string[] = [];
  selectedTargetUuids: string[] = [];

  private readonly toastService = inject(ToastService);
  private readonly translocoService = inject(TranslocoService);
  private readonly cd = inject(ChangeDetectorRef);

  targetStateData = targetStateData;

  ngOnInit(): void {
    this.loadTeacherActivityTargetLinks();
  }

  handleModeChanged(mode: BaseModeEnum): void {
    switch (mode) {
      case BaseModeEnum.VIEW:
        this.loadTeacherActivityTargetLinks();
        break;
      case BaseModeEnum.EDIT:
        this.loadTeacherTargets();
        break;
    }
  }

  loadTeacherActivityTargetLinks(): void {
    this.loadingTeacherActivityTargetLinks = true;
    this.fetchTeacherActivityTargetLinks(
      this.teacherActivityId,
    ).pipe(
      tap(() => this.loadingTeacherActivityTargetLinks = false),
      tap(() => this.cd.markForCheck()),
      takeUntil(this.ngUnsubscribe$),
    ).subscribe({
      next: (teacherActivityTargetLinks) => {
        this.teacherActivityTargetLinks = teacherActivityTargetLinks as TeacherActivityTargetLink[];
        this.teacherActivityTargetLinksCountUpdated.emit(teacherActivityTargetLinks.length);
        this.existingTargetUuids = teacherActivityTargetLinks.map((link) => link.target.id);
        this.selectedTargetUuids = [...this.existingTargetUuids];
      },
      error: () => {
        this.toastService.error(this.translocoService.translate('shared/teacherTargetListErrorToast'));
      },
    });
  }

  loadTeacherTargets(): void {
    this.loadingTeacherTargets = true;
    this.fetchTeacherTargets().pipe(
      tap(() => this.loadingTeacherTargets = false),
      takeUntil(this.ngUnsubscribe$),
    ).subscribe({
      next: (teacherTargets) => {
        this.teacherTargets = teacherTargets as TeacherTarget[];
        this.cd.markForCheck();
      },
      error: () => {
        this.toastService.error(this.translocoService.translate('shared/teacherTargetListErrorToast'));
      },
    });
  }

  handleTeacherTargetSelect(event: Event, targetId: string): void {
    event.stopPropagation();
    const input = event.target as HTMLInputElement;
    const checked = input.checked;

    if (checked) {
      this.selectedTargetUuids.push(targetId);
    } else {
      this.selectedTargetUuids = this.selectedTargetUuids.filter((id) => id !== targetId);
    }
  }

  handleLinkDelete(linkId: string, event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    this.deleteTeacherActivityTargetLink(linkId).pipe(
      takeUntil(this.ngUnsubscribe$),
    ).subscribe({
      next: () => {
        this.loadTeacherActivityTargetLinks();
        this.toastService.success(this.translocoService.translate('shared/teacherActivityTargetLinkDeleteSuccessToast'));
      },
      error: () => {
        this.toastService.error(this.translocoService.translate('shared/teacherActivityTargetLinkDeleteErrorToast'));
      },
    });
  }

  save(): void {
    const createLinkObservables = this.selectedTargetUuids.filter((targetId) => !this.existingTargetUuids.includes(targetId)).map((targetId) => {
      return this.createTeacherActivityTargetLink(this.teacherActivityId, targetId);
    });

    if (createLinkObservables.length <= 0) {
      return;
    }

    forkJoin(createLinkObservables).pipe(
      takeUntil(this.ngUnsubscribe$),
      finalize(() => this.loadTeacherActivityTargetLinks()),
    ).subscribe({
      next: (links: TeacherActivityTargetLink[]) => {
        if (links.length > 0) {
          this.teacherActivityTargetLinksUpdated.emit(links[0].activity);
        }
        this.toastService.success(this.translocoService.translate('shared/teacherActivityTargetLinkCreateSuccessToast'));
      },
      error: () => {
        this.toastService.error(this.translocoService.translate('shared/teacherActivityTargetLinkCreateErrorToast'));
      },
    });
  }

  getTargetStateClass(state: StateEnum | undefined): string {
    switch (state) {
      case 'waiting':
        return 'target-state-waiting';
      case 'in_process':
        return 'target-state-in-process';
      case 'finished':
        return 'target-state-finished';
      case 'incomplete':
        return 'target-state-incomplete';
      default:
        return '';
    }
  }
}
